import { ALL_STATES, type EstadoOt } from '@sime/shared';

/**
 * Parse/serialize for the calendar status-chip filter, stored in the `estados`
 * URL param so it survives navigation round-trips (open ticket → ejecutar →
 * volver) instead of resetting from local component state.
 *
 * The three calendar views (Mensual / Semanal / Hoy) share this contract, and
 * `ALL_STATES` (from @sime/shared, derived from VALID_TRANSITIONS) is the single
 * source of truth for the state universe — never duplicate the list here.
 */

const ALL_STATES_SET: ReadonlySet<string> = new Set(ALL_STATES);

/**
 * `estados` param → the Set of active states the chips render.
 *   absent / empty → every state active (the default: nothing filtered out)
 *   `none`         → empty set (the user explicitly deselected everything)
 *   csv            → exactly those states, ignoring any token not in ALL_STATES
 *                    (defensive against hand-edited or stale URLs)
 */
export function parseEstadosParam(raw: string | null): Set<EstadoOt> {
  if (raw === null || raw === '') return new Set(ALL_STATES);
  if (raw === 'none') return new Set();
  const valid = raw
    .split(',')
    .map((token) => token.trim())
    .filter((token): token is EstadoOt => ALL_STATES_SET.has(token));
  return new Set(valid);
}

/**
 * Active-state Set → `estados` param value (inverse of {@link parseEstadosParam}).
 *   all states active → `undefined` (drop the param, keeping the URL clean)
 *   empty set         → `'none'`
 *   subset            → csv in ALL_STATES order, so toggling chips in any
 *                       sequence yields the same string (stable, no URL churn)
 */
export function serializeEstadosParam(active: Set<string>): string | undefined {
  if (ALL_STATES.every((state) => active.has(state))) return undefined;
  if (active.size === 0) return 'none';
  return ALL_STATES.filter((state) => active.has(state)).join(',');
}
